/**
 * HistoryDrawer.tsx
 *
 * Right-side slide-over panel showing a document's activity history +
 * quick-action buttons.
 *
 * Rendered as a fixed overlay — the table always keeps its full width.
 *
 * Opened by clicking a row's document name.
 * Closed by:
 *   • clicking the [×] button
 *   • clicking the semi-transparent backdrop
 *   • pressing Escape
 */

import React, { useEffect, useState, useCallback } from "react";
import { format, parseISO } from "date-fns";
import type { ExpiryItem } from "../hooks/useExpiryItems";
import { useItemHistory } from "../hooks/useExpiryItems";
import { getItemStatus } from "../utils/expiryUtils";
import StatusBadge from "./StatusBadge";
import OwnerCell from "./OwnerCell";
import HistoryTimeline from "./HistoryTimeline";
import { PencilIcon, ArchiveIcon } from "./Icons";

// ─── Props ────────────────────────────────────────────────────────────────────

interface HistoryDrawerProps {
  item: ExpiryItem | null;
  onClose: () => void;
  onEdit?: (item: ExpiryItem) => void;
  onRenew?: (item: ExpiryItem) => void;
  onSendReminder: (item: ExpiryItem) => void;
  onArchive?: (item: ExpiryItem) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const HistoryDrawer: React.FC<HistoryDrawerProps> = ({
  item,
  onClose,
  onEdit,
  onRenew,
  onSendReminder,
  onArchive,
}) => {
  // CSS transition: 'translate-x-full' → 'translate-x-0' once item is set
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (item) {
      // Small RAF delay ensures CSS transition plays on first open
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    } else {
      setVisible(false);
    }
  }, [item]);

  // ── Lazy-fetch history once the drawer is open ──────────────────────────
  const { data: historyEvents = [], isLoading: isHistoryLoading } =
    useItemHistory(item?.id ?? "", !!item);

  // ── Keyboard: Escape to close ────────────────────────────────────────────
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!item) return;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [item, handleKeyDown]);

  if (!item) return null;

  const status = getItemStatus(item);
  const expiryDate = item.expiry_date
    ? format(parseISO(item.expiry_date), "MMM d, yyyy")
    : "—";
  const owners: any[] = (item as any).owners ?? [];

  /** Run an action then close the drawer */
  const run = (fn: () => void) => {
    fn();
    onClose();
  };

  return (
    <>
      {/* ── Backdrop ──────────────────────────────────────────────────────── */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-300 ${
          visible ? "bg-black/30 opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* ── Panel ─────────────────────────────────────────────────────────── */}
      <div
        className={`fixed right-0 top-0 h-full w-[420px] max-w-[92vw] bg-white z-50 shadow-2xl flex flex-col
          transition-transform duration-300 ease-in-out
          ${visible ? "translate-x-0" : "translate-x-full"}`}
        role="dialog"
        aria-modal="true"
        aria-label={`Activity for ${item.name}`}
      >
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-[#E5E7EB] bg-white flex-shrink-0">
          <div className="min-w-0 pr-3">
            <p className="text-xs text-[#9CA3AF] mb-0.5 uppercase tracking-wider font-medium">
              Document
            </p>
            <h2 className="text-base font-semibold text-[#111827] leading-tight break-words">
              {item.name}
            </h2>
            {item.category && (
              <span className="inline-block mt-1.5 text-xs text-[#6B7280] bg-[#F3F4F6] px-2 py-0.5 rounded-full">
                {item.category}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 mt-0.5 p-1.5 text-[#9CA3AF] hover:text-[#374151] hover:bg-[#F3F4F6] rounded-md transition-colors"
            aria-label="Close panel"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* ── Quick Info ──────────────────────────────────────────────────── */}
        <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB] flex-shrink-0">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
            <div>
              <p className="text-xs text-[#9CA3AF] mb-0.5">Expiry Date</p>
              <p className="text-sm font-medium text-[#111827]">{expiryDate}</p>
            </div>
            <div>
              <p className="text-xs text-[#9CA3AF] mb-0.5">Status</p>
              <StatusBadge status={status.status} label={status.label} />
            </div>
            {owners.length > 0 && (
              <div className="col-span-2">
                <p className="text-xs text-[#9CA3AF] mb-1">Owners</p>
                <OwnerCell owners={owners} />
              </div>
            )}
          </div>
        </div>

        {/* ── History timeline (scrollable) ────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="px-5 pt-4 pb-1.5">
            <h3 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">
              Activity History
            </h3>
          </div>

          <div className="px-5 pb-8">
            {isHistoryLoading ? (
              /* Loading skeleton */
              <div className="animate-pulse space-y-5 pt-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex gap-3">
                    <div className="h-7 w-7 rounded-full bg-[#E5E7EB] flex-shrink-0" />
                    <div className="flex-1 space-y-2 pt-0.5">
                      <div className="h-3 bg-[#E5E7EB] rounded w-2/3" />
                      <div className="h-3 bg-[#E5E7EB] rounded w-full" />
                      <div className="h-3 bg-[#E5E7EB] rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <HistoryTimeline events={historyEvents} />
            )}
          </div>
        </div>

        {/* ── Action footer ────────────────────────────────────────────────── */}
        <div className="flex-shrink-0 border-t border-[#E5E7EB] px-5 py-3 bg-white">
          <p className="text-xs text-[#9CA3AF] mb-2">Quick actions</p>
          <div className="flex flex-wrap gap-2">
            {/* Edit — hidden for viewers */}
            {onEdit && (
              <button
                onClick={() => run(() => onEdit(item))}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-[#E5E7EB] text-[#374151] rounded-md hover:bg-[#F9FAFB] hover:border-[#D1D5DB] transition-colors"
              >
                <PencilIcon className="h-3.5 w-3.5" />
                Edit
              </button>
            )}

            {/* Renew — hidden for viewers */}
            {onRenew && (
              <button
                onClick={() => run(() => onRenew(item))}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-[#E5E7EB] text-[#374151] rounded-md hover:bg-[#F0FDF4] hover:border-[#86EFAC] hover:text-[#15803D] transition-colors"
              >
                <svg
                  className="h-3.5 w-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Renew
              </button>
            )}

            {/* Send Reminder */}
            <button
              onClick={() => run(() => onSendReminder(item))}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-[#E5E7EB] text-[#374151] rounded-md hover:bg-[#EFF6FF] hover:border-[#BFDBFE] hover:text-[#2563EB] transition-colors"
            >
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              Send Mail
            </button>

            {/* Archive — hidden for viewers */}
            {onArchive && (
              <button
                onClick={() => run(() => onArchive(item))}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-[#E5E7EB] text-[#374151] rounded-md hover:bg-[#FEF2F2] hover:border-[#FECACA] hover:text-[#DC2626] transition-colors"
              >
                <ArchiveIcon className="h-3.5 w-3.5" />
                Archive
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default HistoryDrawer;
